/**
 * B631 leg (f) — tenant webhooks (CLASS A, DEFAULT RECORDED 2026-10-06 by the
 * design seat under Pathik's 2026-10-06 instruction; he overrules with "no on
 * B631f"): a business registers an https endpoint it owns and hears
 * `order.confirmed`, `order.cancelled` and `ticket.redeemed`. Each delivery is
 * signed HMAC-SHA256 over `<t>.<raw body>` with a per-endpoint `whsec_`
 * secret.
 *
 * CUSTODY — "digest stored, shown once" as the API key has it, with the one
 * difference HMAC forces: the SENDER must hold the secret to sign, so a
 * digest alone cannot be all that exists. The table keeps only hash + last4
 * (this record); the secret itself lives in Secrets Manager at the handover
 * name, which the ops role can CREATE but never read, and only the delivery
 * role reads (its `webhooks/` leaves, nothing else). No response carries it.
 *
 * Pure: no clock, no randomness, no crypto of its own — the hasher and the
 * HMAC are injected.
 */
import type { SecretHasher } from "./devices.js";
import type { EventEnvelope } from "./events.js";

export const WEBHOOK_EVENTS = ["order.confirmed", "order.cancelled", "ticket.redeemed"] as const;
export type WebhookEventType = (typeof WEBHOOK_EVENTS)[number];

/** The §12 integration name that feeds each webhook event (the bus's detailType). */
const EVENT_OF_DETAIL: Readonly<Record<string, WebhookEventType>> = Object.freeze({
  OrderConfirmed: "order.confirmed",
  OrderCancelled: "order.cancelled",
  RedemptionRecorded: "ticket.redeemed",
});
/** What the stack's EventBridge rule carries into the webhook queue. */
export const WEBHOOK_DETAIL_TYPES = Object.freeze(Object.keys(EVENT_OF_DETAIL)) as readonly string[];

export const WEBHOOK_SECRET_PREFIX = "whsec_";
export const WEBHOOK_SIGNATURE_HEADER = "Transact-Signature";
/** A receiver refuses a signature stamped further than this from its own clock. */
export const WEBHOOK_TOLERANCE_SECONDS = 300;
export const WEBHOOK_NAME_MAX = 60;
export const WEBHOOK_URL_MAX = 2048;

const TOKEN_PART = /^[A-Za-z0-9_-]{16,128}$/;
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
const PRIVATE_SUFFIXES = [".localhost", ".local", ".internal", ".lan", ".home.arpa"];

export type WebhookMode = "test" | "live";

export interface WebhookEndpoint {
  readonly tenantId: string;
  readonly endpointId: string;
  readonly name: string;
  readonly url: string;
  /** Vocabulary order, deduplicated, at least one. */
  readonly events: readonly WebhookEventType[];
  readonly mode: WebhookMode;
  /** hash(secret) — the secret itself is never in this record. */
  readonly secretHash: string;
  readonly last4: string;
  readonly createdAt: number;
  readonly createdBy: string;
  readonly revokedAt?: number;
  readonly revokedReason?: string;
}

type Judged<K extends string, V> = ({ readonly ok: true } & { readonly [k in K]: V }) | { readonly ok: false; readonly why: string };

/**
 * An endpoint's address: https on 443, no credentials, a public host name —
 * never an IP literal, localhost or a private suffix (the delivery Lambda
 * must not be pointed at anything inside the platform). The fragment drops.
 */
export function webhookUrl(raw: string): Judged<"url", string> {
  const trimmed = raw.trim();
  if (trimmed === "") return { ok: false, why: "Paste the address your server listens on, starting https://" };
  if (trimmed.length > WEBHOOK_URL_MAX) return { ok: false, why: "That address is too long — keep it under 2,048 characters." };
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return { ok: false, why: "That isn't a web address — it should start https://" };
  }
  if (url.protocol !== "https:") return { ok: false, why: "The address must start https:// — deliveries are never sent unencrypted." };
  if (url.username !== "" || url.password !== "") return { ok: false, why: "Take the user name and password out of the address — every delivery is signed instead." };
  if (url.port !== "" && url.port !== "443") return { ok: false, why: "Use the standard https port (443) — other ports are refused." };
  const host = url.hostname.toLowerCase();
  const privateHost =
    host.startsWith("[") ||
    host.includes(":") ||
    IPV4.test(host) ||
    host === "localhost" ||
    !host.includes(".") ||
    PRIVATE_SUFFIXES.some((s) => host.endsWith(s));
  if (privateHost) return { ok: false, why: "Use a public host name (like hooks.example.com) — addresses, localhost and private names are refused." };
  url.hash = "";
  return { ok: true, url: url.href };
}

export function webhookEndpointName(raw: string): Judged<"name", string> {
  const name = raw.replace(/\s+/g, " ").trim();
  if (name === "") return { ok: false, why: "Name this endpoint — say what listens there, like Our CRM." };
  if (name.length > WEBHOOK_NAME_MAX) return { ok: false, why: `Keep the name to ${String(WEBHOOK_NAME_MAX)} characters.` };
  return { ok: true, name };
}

export function webhookEvents(raw: readonly string[]): Judged<"events", readonly WebhookEventType[]> {
  if (raw.length === 0) return { ok: false, why: "Pick at least one event to send." };
  const unknown = raw.find((e) => !(WEBHOOK_EVENTS as readonly string[]).includes(e));
  if (unknown !== undefined) return { ok: false, why: `${unknown} is not an event a webhook can send.` };
  return { ok: true, events: Object.freeze(WEBHOOK_EVENTS.filter((e) => raw.includes(e))) };
}

export function mintWebhookEndpoint(input: {
  readonly endpointId: string;
  readonly secret: string;
  readonly tenantId: string;
  readonly name: string;
  readonly url: string;
  readonly events: readonly string[];
  readonly mode: WebhookMode;
  readonly createdBy: string;
  readonly now: number;
  readonly hash: SecretHasher;
}): WebhookEndpoint {
  if (!TOKEN_PART.test(input.endpointId) || !TOKEN_PART.test(input.secret)) {
    throw new RangeError("endpoint id and secret must be 16–128 url-safe characters");
  }
  if (input.tenantId === "") throw new RangeError("an endpoint names one business");
  const name = webhookEndpointName(input.name);
  if (!name.ok) throw new RangeError(name.why);
  const url = webhookUrl(input.url);
  if (!url.ok) throw new RangeError(url.why);
  const events = webhookEvents(input.events);
  if (!events.ok) throw new RangeError(events.why);
  return Object.freeze({
    tenantId: input.tenantId,
    endpointId: input.endpointId,
    name: name.name,
    url: url.url,
    events: events.events,
    mode: input.mode,
    secretHash: input.hash(input.secret),
    last4: input.secret.slice(-4),
    createdAt: input.now,
    createdBy: input.createdBy,
  });
}

/** What the business is handed (and what signs): `whsec_<secret>`. */
export function webhookSigningSecret(secret: string): string {
  return `${WEBHOOK_SECRET_PREFIX}${secret}`;
}

export type Hmac = (key: string, message: string) => string;

/** `t=<unix seconds>,v1=<hex HMAC-SHA256(secret, "<t>.<body>")>` — the Stripe-shaped header receivers already know how to check. */
export function webhookSignature(input: { readonly secret: string; readonly timestamp: number; readonly body: string; readonly hmac: Hmac }): string {
  const t = String(Math.floor(input.timestamp));
  return `t=${t},v1=${input.hmac(input.secret, `${t}.${input.body}`)}`;
}

export type WebhookSignatureVerdict = { readonly ok: true } | { readonly ok: false; readonly why: "malformed" | "stale" | "mismatch" };

/** The receiver's check, as the docs print it — and the walls run it. */
export function verifyWebhookSignature(input: {
  readonly header: string;
  readonly secret: string;
  readonly body: string;
  readonly nowSeconds: number;
  readonly hmac: Hmac;
  readonly equal?: (a: string, b: string) => boolean;
  readonly toleranceSeconds?: number;
}): WebhookSignatureVerdict {
  const parts = new Map<string, string[]>();
  for (const piece of input.header.split(",")) {
    const at = piece.indexOf("=");
    if (at <= 0) continue;
    const k = piece.slice(0, at).trim();
    parts.set(k, [...(parts.get(k) ?? []), piece.slice(at + 1).trim()]);
  }
  const t = parts.get("t")?.[0];
  const sigs = parts.get("v1") ?? [];
  if (t === undefined || !/^\d+$/.test(t) || sigs.length === 0) return { ok: false, why: "malformed" };
  if (Math.abs(input.nowSeconds - Number(t)) > (input.toleranceSeconds ?? WEBHOOK_TOLERANCE_SECONDS)) return { ok: false, why: "stale" };
  const expected = input.hmac(input.secret, `${t}.${input.body}`);
  const equal = input.equal ?? ((a: string, b: string) => a === b);
  return sigs.some((s) => equal(s, expected)) ? { ok: true } : { ok: false, why: "mismatch" };
}

export interface WebhookEventBody {
  /** The bus event's id — stable across retries, so a receiver can dedupe. */
  readonly id: string;
  readonly type: WebhookEventType;
  readonly createdAt: string;
  readonly livemode: boolean;
  /** Thin by design: ids to fetch with an API key, never a buyer's details. */
  readonly data: Readonly<Record<string, string>>;
}

/** The webhook event a bus envelope becomes, or undefined when no webhook event names it. */
export function webhookEvent(envelope: EventEnvelope, livemode: boolean, extra: Readonly<Record<string, string>> = {}): WebhookEventBody | undefined {
  const type = EVENT_OF_DETAIL[envelope.type];
  if (type === undefined) return undefined;
  const p = envelope.payload;
  const data: Record<string, string> = {};
  if (typeof p["orderId"] === "string") data["orderId"] = p["orderId"];
  if (type === "ticket.redeemed" && typeof p["entitlementId"] === "string") data["ticketId"] = p["entitlementId"];
  return Object.freeze({
    id: envelope.eventId,
    type,
    createdAt: new Date(envelope.occurredAt).toISOString(),
    livemode,
    data: Object.freeze({ ...data, ...extra }),
  });
}

export function webhookDeliverable(endpoint: WebhookEndpoint, type: WebhookEventType): boolean {
  return endpoint.revokedAt === undefined && endpoint.events.includes(type);
}

/* ---------------------------------------------------------------- store */

export class DuplicateWebhookEndpointError extends Error {
  constructor(endpointId: string) {
    super(`Webhook endpoint ${endpointId} already exists`);
    this.name = "DuplicateWebhookEndpointError";
  }
}

export class WebhookEndpointNotFoundError extends Error {
  constructor(endpointId: string) {
    super(`No such webhook endpoint ${endpointId} for this business`);
    this.name = "WebhookEndpointNotFoundError";
  }
}

export interface WebhookEndpointStore {
  create(endpoint: WebhookEndpoint): Promise<void>;
  get(tenantId: string, endpointId: string): Promise<WebhookEndpoint | undefined>;
  /** Ascending by creation then id. */
  listByTenant(tenantId: string): Promise<readonly WebhookEndpoint[]>;
  /** Idempotent — the FIRST mark stands. */
  revoke(tenantId: string, endpointId: string, at: number, reason?: string): Promise<void>;
}

export class InMemoryWebhookEndpointStore implements WebhookEndpointStore {
  private readonly rows = new Map<string, WebhookEndpoint>();
  private static k(tenantId: string, endpointId: string): string {
    return `${tenantId}\u0000${endpointId}`;
  }
  create(endpoint: WebhookEndpoint): Promise<void> {
    const k = InMemoryWebhookEndpointStore.k(endpoint.tenantId, endpoint.endpointId);
    if (this.rows.has(k)) return Promise.reject(new DuplicateWebhookEndpointError(endpoint.endpointId));
    this.rows.set(k, Object.freeze({ ...endpoint }));
    return Promise.resolve();
  }
  get(tenantId: string, endpointId: string): Promise<WebhookEndpoint | undefined> {
    return Promise.resolve(this.rows.get(InMemoryWebhookEndpointStore.k(tenantId, endpointId)));
  }
  listByTenant(tenantId: string): Promise<readonly WebhookEndpoint[]> {
    return Promise.resolve(
      [...this.rows.values()].filter((e) => e.tenantId === tenantId).sort((a, b) => a.createdAt - b.createdAt || a.endpointId.localeCompare(b.endpointId)),
    );
  }
  revoke(tenantId: string, endpointId: string, at: number, reason?: string): Promise<void> {
    const k = InMemoryWebhookEndpointStore.k(tenantId, endpointId);
    const e = this.rows.get(k);
    if (e === undefined) return Promise.reject(new WebhookEndpointNotFoundError(endpointId));
    if (e.revokedAt === undefined) this.rows.set(k, Object.freeze({ ...e, revokedAt: at, ...(reason !== undefined ? { revokedReason: reason } : {}) }));
    return Promise.resolve();
  }
}
